import { useAccountingCompany } from '../components/HoldingAccounting';
import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';

export default function FinancialPayers() {
  const companyId = useAccountingCompany();
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  const base = `${API}/companies/${companyId}/finance`;

  const [payers, setPayers] = useState([]);
  const [payerTypes, setPayerTypes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [projectForm, setProjectForm] = useState({ project_id: '', overall_price: '', discount_type: 'none', discount_value: '', tax_type: 'none', tax_value: '', tax_mode: 'exclusive' });
  const [form, setForm] = useState({ name: '', payer_type_id: '', email: '', phone: '', address: '', notes: '' });

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      fetch(`${base}/payers`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : []),
      fetch(`${base}/settings/payer-types`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : []),
      fetch(`${base}/projects`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : { data: [] }),
    ]).then(([p, types, projs]) => {
      setPayers(Array.isArray(p) ? p : []);
      setPayerTypes(Array.isArray(types) ? types : []);
      const projList = projs.data ? projs.data : Array.isArray(projs) ? projs : [];
      setProjects(projList);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { if (companyId) fetchAll(); }, [companyId]);

  const savePayer = async () => {
    const url = editing ? `${base}/payers/${editing.id}` : `${base}/payers`;
    const method = editing ? 'PUT' : 'POST';
    const res = await fetch(url, { method, headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    if (res.ok) { setShowForm(false); setEditing(null); setForm({ name: '', payer_type_id: '', email: '', phone: '', address: '', notes: '' }); fetchAll(); }
  };

  const viewDetail = async (id) => {
    const res = await fetch(`${base}/payers/${id}`, { headers: getAuthHeaders() });
    if (res.ok) { const d = await res.json(); setDetail(d); }
  };

  const addProject = async () => {
    const res = await fetch(`${base}/payers/${detail.id}/projects`, { method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(projectForm) });
    if (res.ok) { setShowProjectForm(false); setProjectForm({ project_id: '', overall_price: '', discount_type: 'none', discount_value: '', tax_type: 'none', tax_value: '', tax_mode: 'exclusive' }); viewDetail(detail.id); }
  };

  const editPayer = (p) => { setEditing(p); setForm({ name: p.name, payer_type_id: p.payer_type_id || '', email: p.email || '', phone: p.phone || '', address: p.address || '', notes: p.notes || '' }); setShowForm(true); };

  const fmt = (v) => '$' + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });
  const sectionStyle = { background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 20 };
  const btnStyle = { padding: '8px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 };
  const inputStyle = { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.85rem', boxSizing: 'border-box' };
  const thStyle = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e2e8f0', fontSize: '0.8rem', color: '#64748b', fontWeight: 600 };
  const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem' };

  if (detail) {
    return (
      <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
        <button onClick={() => setDetail(null)} style={{ ...btnStyle, background: '#e2e8f0', marginBottom: 16 }}>← Back to List</button>
        <div style={sectionStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ margin: '0 0 4px', fontSize: '1.3rem', fontWeight: 700 }}>{detail.name}</h2>
              <div style={{ color: '#64748b', fontSize: '0.85rem' }}>{detail.payer_type?.name || 'N/A'} • {detail.email || ''} • {detail.phone || ''}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Credit Balance</div>
              <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#8b5cf6' }}>{fmt(detail.credit_balance)}</div>
            </div>
          </div>
        </div>

        {/* Financial Summary */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div style={{ ...sectionStyle, borderLeft: '4px solid #22c55e', marginBottom: 0 }}>
            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Total Expected</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#22c55e' }}>{fmt(detail.total_expected)}</div>
          </div>
          <div style={{ ...sectionStyle, borderLeft: '4px solid #3b82f6', marginBottom: 0 }}>
            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Total Paid</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#3b82f6' }}>{fmt(detail.total_paid)}</div>
          </div>
          <div style={{ ...sectionStyle, borderLeft: '4px solid #f59e0b', marginBottom: 0 }}>
            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Outstanding</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#f59e0b' }}>{fmt(detail.total_outstanding)}</div>
          </div>
        </div>

        {/* Projects */}
        <div style={sectionStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>📁 Enrolled Projects</h3>
            <button onClick={() => setShowProjectForm(true)} style={{ ...btnStyle, background: '#3b82f6', color: '#fff', fontSize: '0.8rem' }}>+ Add to Project</button>
          </div>
          {Array.isArray(detail.projects) && detail.projects.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={thStyle}>Project</th><th style={{ ...thStyle, textAlign: 'right' }}>Overall Price</th><th style={{ ...thStyle, textAlign: 'right' }}>Discount</th><th style={{ ...thStyle, textAlign: 'right' }}>Final Price</th></tr></thead>
                <tbody>
                  {detail.projects.map(pp => (
                    <tr key={pp.id}><td style={tdStyle}>{pp.project?.name || pp.name || '-'}</td><td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(pp.pivot?.overall_price || pp.overall_price)}</td><td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(pp.pivot?.discount_amount || pp.discount_amount)}</td><td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{fmt(pp.pivot?.final_price || pp.final_price)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No projects assigned</div>}
        </div>

        {/* Payment History */}
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>💳 Payment History</h3>
          {Array.isArray(detail.payment_history) && detail.payment_history.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={thStyle}>Ref</th><th style={thStyle}>Date</th><th style={thStyle}>Project</th><th style={{ ...thStyle, textAlign: 'right' }}>Amount</th><th style={thStyle}>Method</th><th style={thStyle}>Status</th></tr></thead>
                <tbody>
                  {detail.payment_history.map(t => (
                    <tr key={t.id}>
                      <td style={tdStyle}><span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#6366f1' }}>{t.reference}</span></td>
                      <td style={tdStyle}>{t.transaction_date ? new Date(t.transaction_date).toLocaleDateString() : '-'}</td>
                      <td style={tdStyle}>{t.project?.name || '-'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: '#22c55e' }}>{fmt(t.amount)}</td>
                      <td style={tdStyle}>{t.payment_method?.name || '-'}</td>
                      <td style={tdStyle}>{t.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No payments yet</div>}
        </div>

        {/* Add Project Modal */}
        {showProjectForm && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 32, width: '90%', maxWidth: 500 }}>
              <h3 style={{ margin: '0 0 20px' }}>Add to Project</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Project *</label><select value={projectForm.project_id} onChange={e => setProjectForm({ ...projectForm, project_id: e.target.value })} style={inputStyle}><option value="">Select</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
                <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Overall Price</label><input type="number" step="0.01" value={projectForm.overall_price} onChange={e => setProjectForm({ ...projectForm, overall_price: e.target.value })} style={inputStyle} /></div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Discount Type</label><select value={projectForm.discount_type} onChange={e => setProjectForm({ ...projectForm, discount_type: e.target.value })} style={inputStyle}><option value="none">None</option><option value="fixed">Fixed</option><option value="percentage">Percentage</option></select></div>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Discount Value</label><input type="number" step="0.01" value={projectForm.discount_value} onChange={e => setProjectForm({ ...projectForm, discount_value: e.target.value })} style={inputStyle} /></div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Tax Type</label><select value={projectForm.tax_type} onChange={e => setProjectForm({ ...projectForm, tax_type: e.target.value })} style={inputStyle}><option value="none">None</option><option value="fixed">Fixed</option><option value="percentage">Percentage</option></select></div>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Tax Value</label><input type="number" step="0.01" value={projectForm.tax_value} onChange={e => setProjectForm({ ...projectForm, tax_value: e.target.value })} style={inputStyle} /></div>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Tax Mode</label><select value={projectForm.tax_mode} onChange={e => setProjectForm({ ...projectForm, tax_mode: e.target.value })} style={inputStyle}><option value="exclusive">Exclusive</option><option value="inclusive">Inclusive</option></select></div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button onClick={() => setShowProjectForm(false)} style={{ ...btnStyle, background: '#e2e8f0' }}>Cancel</button>
                <button onClick={addProject} style={{ ...btnStyle, background: '#3b82f6', color: '#fff' }}>Add</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1e293b' }}>👤 Payers / Customers</h2>
        <button onClick={() => { setEditing(null); setForm({ name: '', payer_type_id: '', email: '', phone: '', address: '', notes: '' }); setShowForm(true); }} style={{ ...btnStyle, background: '#3b82f6', color: '#fff' }}>+ New Payer</button>
      </div>

      <div style={sectionStyle}>
        {initialLoading ? <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr><th style={thStyle}>Name</th><th style={thStyle}>Type</th><th style={thStyle}>Email</th><th style={thStyle}>Phone</th><th style={{ ...thStyle, textAlign: 'right' }}>Total Expected</th><th style={{ ...thStyle, textAlign: 'right' }}>Total Paid</th><th style={{ ...thStyle, textAlign: 'right' }}>Outstanding</th><th style={thStyle}>Actions</th></tr>
              </thead>
              <tbody>
                {payers.length === 0 ? (
                  <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No payers found</td></tr>
                ) : payers.map(p => (
                  <tr key={p.id}>
                    <td style={{ ...tdStyle, fontWeight: 500, cursor: 'pointer', color: '#3b82f6' }} onClick={() => viewDetail(p.id)}>{p.name}</td>
                    <td style={tdStyle}>{p.payer_type?.name || '-'}</td>
                    <td style={tdStyle}>{p.email || '-'}</td>
                    <td style={tdStyle}>{p.phone || '-'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(p.total_expected)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: '#22c55e' }}>{fmt(p.total_paid)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: '#f59e0b' }}>{fmt(p.total_outstanding)}</td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => viewDetail(p.id)} style={{ ...btnStyle, background: '#e2e8f0', padding: '4px 10px', fontSize: '0.8rem' }}>👁️</button>
                        <button onClick={() => editPayer(p)} style={{ ...btnStyle, background: '#e2e8f0', padding: '4px 10px', fontSize: '0.8rem' }}>✏️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, width: '90%', maxWidth: 520 }}>
            <h3 style={{ margin: '0 0 20px' }}>{editing ? 'Edit Payer' : 'New Payer'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Name *</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} /></div>
              <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Type</label><select value={form.payer_type_id} onChange={e => setForm({ ...form, payer_type_id: e.target.value })} style={inputStyle}><option value="">Select</option>{payerTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Email</label><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={inputStyle} /></div>
                <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Phone</label><input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} style={inputStyle} /></div>
              </div>
              <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Address</label><input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} style={inputStyle} /></div>
              <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Notes</label><textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={{ ...inputStyle, resize: 'vertical' }} /></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={() => { setShowForm(false); setEditing(null); }} style={{ ...btnStyle, background: '#e2e8f0' }}>Cancel</button>
              <button onClick={savePayer} style={{ ...btnStyle, background: '#3b82f6', color: '#fff' }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
